import { useRef } from "react";
import { motion, useMotionValue, useTransform, useSpring, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Fence, Hammer, Wrench, Plus } from "lucide-react";
import { SERVICES, TRUST } from "@/data";
import { Reveal, Eyebrow } from "@/components/site/motion-lib";

const WindowIcon = ({ size = 24, strokeWidth = 1, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <rect x="3" y="3" width="18" height="18" rx="1.5" />
    <line x1="12" y1="3" x2="12" y2="21" />
    <line x1="3" y1="12" x2="21" y2="12" />
  </svg>
);

const GlassIcon = ({ size = 24, strokeWidth = 1, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <rect x="4" y="3" width="16" height="18" rx="1.5" />
    <line x1="8.5" y1="6.5" x2="15" y2="13" />
    <line x1="8.5" y1="11" x2="12" y2="14.5" />
  </svg>
);

const StairsIcon = ({ size = 24, strokeWidth = 1, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M3 20h4v-4h4v-4h4v-4h4V4" />
    <path d="M3 20v-4h4" />
    <path d="M11 12h-4v4" />
    <path d="M15 8h-4v4" />
  </svg>
);

const ICONS = {
  "porte-blindate": WindowIcon,
  serrature: GlassIcon,
  cancelli: Fence,
  "ferro-battuto": StairsIcon,
  restauro: Wrench,
  "pronto-intervento": Plus,
};

/* ---------------- Trust marquee ---------------- */
export function TrustMarquee() {
  const items = [...TRUST, ...TRUST];
  return (
    <div data-testid="trust-marquee" className="border-y border-[#F5F1EA]/10 bg-[#1c1c1c] py-5 overflow-hidden">
      <div className="marquee-track marquee-slow">
        {items.map((t, i) => (
          <span key={i} className="inline-flex items-center gap-4 px-8 text-[#F5F1EA]/55">
            <span className="h-1.5 w-1.5 rotate-45 bg-[#C89B4A]" />
            <span className="font-display text-lg md:text-xl tracking-tight">{t}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Service card with 3D tilt ---------------- */
function ServiceCard({ s, index }) {
  const Icon = ICONS[s.id] || Hammer;
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [7, -7]), { stiffness: 150, damping: 15 });
  const ry = useSpring(useTransform(mx, [0, 1], [-7, 7]), { stiffness: 150, damping: 15 });
  const glowX = useTransform(mx, [0, 1], ["0%", "100%"]);
  const glowY = useTransform(my, [0, 1], ["0%", "100%"]);
  const glow = useTransform(
    [glowX, glowY],
    ([x, y]) => `radial-gradient(240px circle at ${x} ${y}, rgba(200,155,74,0.14), transparent 70%)`
  );

  const onMove = (e) => {
    if (reduce || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const reset = () => { mx.set(0.5); my.set(0.5); };

  return (
    <Reveal delay={(index % 3) * 0.08} className="[perspective:1200px]">
      <motion.article
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={reset}
        style={reduce ? {} : { rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        data-testid={`service-card-${s.id}`}
        className="group relative h-full metal-border rounded-2xl p-8 hover-glow overflow-hidden"
      >
        <motion.div
          className="pointer-events-none absolute -inset-px opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{ background: reduce ? "none" : glow }}
          aria-hidden="true"
        />
        <div className="flex items-start justify-between">
          <Icon size={34} strokeWidth={1} className="text-[#C89B4A]" />
          <span className="font-display text-sm text-[#F5F1EA]/30">{s.n}</span>
        </div>
        <h3 className="font-display text-2xl font-bold text-[#F5F1EA] mt-8">{s.title}</h3>
        <p className="mt-3 text-[#F5F1EA]/65 leading-relaxed text-[15px]">{s.text}</p>
        <a
          href="#contatti"
          data-testid={`service-link-${s.id}`}
          className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#C89B4A] group-hover:gap-3 transition-all duration-300"
        >
          Scopri di più <ArrowUpRight size={16} strokeWidth={1.75} />
        </a>
      </motion.article>
    </Reveal>
  );
}

export function Services() {
  return (
    <section id="servizi" data-testid="services-section" className="relative py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-2xl">
          <Reveal><Eyebrow>Cosa facciamo</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#F5F1EA] mt-5 text-balance">
              Professionalità e serietà, con mani da artigiano
            </h2>
          </Reveal>
        </div>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => <ServiceCard key={s.id} s={s} index={i} />)}
        </div>
      </div>
    </section>
  );
}

/* ---------------- About / manifesto ---------------- */
const ABOUT_IMG = "https://customer-assets-rejwkqb3.emergentagent.net/job_fabbro-24h-premium/artifacts/4ssnh203_IMG_9358.JPG";

const CHAPTERS = [
  { n: "01", title: "L'esperienza, l'artigianalità e la manualità", text: "Officine Galloni nasce nel 1979; da allora esperienza, artigianalità e manualità accompagnano ogni nostro lavoro." },
  { n: "02", title: "Macchinari specializzati e all'avanguardia", text: "La nostra officina vanta macchinari all'avanguardia e specialistici per il nostro settore che vengono utilizzati sapientemente dai nostri operai durante le nostre lavorazioni." },
  { n: "03", title: "Serietà, disponibilità e professionalità sempre", text: "Il nostro lavoro manuale è sempre affiancato dalla nostra serietà, disponibilità e professionalità che vengono dimostrati ogni giorno dai nostri lavoratori." },
];

export function About() {
  return (
    <section id="chi-siamo" data-testid="about-section" className="relative py-24 md:py-32 border-t border-[#F5F1EA]/10">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        <div className="lg:sticky lg:top-28">
          <Reveal><Eyebrow>Chi siamo</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#F5F1EA] mt-5 leading-[1.05] text-balance">
              Non vendiamo prodotti. <span className="italic text-[#C89B4A]">Li creiamo da 0.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative mt-9 rounded-2xl overflow-hidden metal-border">
              <img src={ABOUT_IMG} alt="Artigiano fabbro al lavoro nell'officina" className="w-full h-[380px] object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#141414]/70 to-transparent" />
              <div className="absolute bottom-5 left-5">
                <p className="font-display text-2xl font-bold text-[#F5F1EA]">Montespertoli, Firenze</p>
                <p className="text-sm text-[#F5F1EA]/60">La nostra officina, dal 1979</p>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="space-y-2">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.08}>
              <div className="group border-t border-[#F5F1EA]/10 py-8 hover:border-[#C89B4A]/40 transition-colors duration-500">
                <div className="flex items-baseline gap-6">
                  <span className="font-display text-3xl font-bold text-[#C89B4A]/40 group-hover:text-[#C89B4A] transition-colors">{c.n}</span>
                  <div>
                    <h3 className="font-display text-2xl font-bold text-[#F5F1EA]">{c.title}</h3>
                    <p className="mt-3 text-[#F5F1EA]/65 leading-relaxed">{c.text}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
